import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PublicHeader, PublicFooter, ChapterBadge } from "@/components/PublicChrome";
import {
  clientAuthApi,
  clientApi,
  saveClientSession,
  clientLogout,
  getClientUser,
} from "@/lib/clientApi";
import {
  Compass,
  LogIn,
  UserPlus,
  ShieldAlert,
  KeyRound,
  MapPin,
  CalendarDays,
  LogOut,
  Ticket,
} from "lucide-react";

const STATUT_TONE = {
  PAYE: "bg-vert-50/15 text-vert-50 border-vert-50/40",
  EN_ATTENTE: "bg-or/15 text-or border-or/40",
  ANNULE: "bg-oxyde/15 text-oxyde border-oxyde/50",
  REMBOURSE: "bg-parchemin/5 text-parchemin/70 border-parchemin/20",
};

export default function Compte() {
  const [user, setUser] = useState(getClientUser());
  const [resas, setResas] = useState([]);
  const [loading, setLoading] = useState(false);

  async function loadReservations() {
    setLoading(true);
    try {
      const data = await clientApi.reservations();
      setResas(data);
    } catch {
      setResas([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user) loadReservations();
  }, [user]);

  function onAuthed(data) {
    saveClientSession(data);
    setUser(data.user);
  }

  function logout() {
    clientLogout();
    setUser(null);
    setResas([]);
  }

  return (
    <div className="min-h-screen bg-noir text-parchemin bg-grain flex flex-col" data-testid="compte-page">
      <PublicHeader current="compte" />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-12">
        {!user ? (
          <AuthCard onAuthed={onAuthed} />
        ) : (
          <ClientDashboard user={user} resas={resas} loading={loading} onLogout={logout} />
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

/* ─── Auth (connexion / inscription) ─────────────────────────── */
function AuthCard({ onAuthed }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [prenom, setPrenom] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data =
        mode === "login"
          ? await clientAuthApi.login(email, password)
          : await clientAuthApi.register(email, password, prenom);
      onAuthed(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : mode === "login"
            ? "Identifiants invalides"
            : "Inscription impossible"
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-5">
          <ChapterBadge testId="compte-chapter">Chapitre voyageur · Mon carnet</ChapterBadge>
        </div>
        <h1 className="font-display font-medium text-3xl md:text-4xl text-parchemin leading-tight">
          {mode === "login" ? "Retrouvez votre" : "Ouvrez votre"}
          <br />
          <span className="italic text-or">carnet de voyage</span>
        </h1>
        <p className="mt-4 text-sm text-parchemin/60">
          Vos réservations, vos codes d'accès et vos aventures, au même endroit.
        </p>
      </div>

      <div className="flex rounded-sm border border-or/20 overflow-hidden mb-6" data-testid="compte-tabs">
        <button
          onClick={() => setMode("login")}
          data-testid="tab-login"
          className={`flex-1 py-2.5 text-xs font-mono uppercase tracking-widest transition ${
            mode === "login" ? "bg-or/15 text-or" : "text-parchemin/60 hover:text-or"
          }`}
        >
          Connexion
        </button>
        <button
          onClick={() => setMode("register")}
          data-testid="tab-register"
          className={`flex-1 py-2.5 text-xs font-mono uppercase tracking-widest transition ${
            mode === "register" ? "bg-or/15 text-or" : "text-parchemin/60 hover:text-or"
          }`}
        >
          Créer un compte
        </button>
      </div>

      <div className="border border-or/25 bg-noir-50/60 backdrop-blur-sm rounded-sm p-7">
        <form onSubmit={submit} className="flex flex-col gap-5" data-testid="compte-form">
          {error && (
            <div
              className="flex items-start gap-2 text-sm py-3 px-4 rounded-sm bg-oxyde/10 border border-oxyde/40 text-parchemin/90"
              data-testid="compte-error"
            >
              <ShieldAlert className="w-4 h-4 mt-0.5 text-oxyde flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {mode === "register" && (
            <Field label="Prénom">
              <input
                data-testid="compte-prenom"
                type="text"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                required
                className={inputCls}
              />
            </Field>
          )}
          <Field label="Email">
            <input
              data-testid="compte-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className={inputCls}
            />
          </Field>
          <Field label="Mot de passe">
            <input
              data-testid="compte-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className={inputCls}
            />
          </Field>

          <button
            data-testid="compte-submit"
            type="submit"
            disabled={loading}
            className={`group mt-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-sm font-medium text-sm tracking-wide transition-all duration-300 ${
              loading ? "bg-or/40 text-noir/60 cursor-not-allowed" : "bg-or hover:bg-or-100 text-noir"
            }`}
          >
            {loading ? (
              "Un instant…"
            ) : mode === "login" ? (
              <>
                <LogIn className="w-4 h-4" />
                Ouvrir mon carnet
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Créer mon carnet
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Espace connecté ────────────────────────────────────────── */
function ClientDashboard({ user, resas, loading, onLogout }) {
  const navigate = useNavigate();

  return (
    <div data-testid="compte-dashboard">
      <div className="flex items-end justify-between gap-4 pb-6 border-b border-or/15">
        <div>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
            Chapitre voyageur · Mon carnet
          </span>
          <h1 className="mt-2 font-display font-medium text-3xl md:text-4xl text-parchemin leading-tight">
            Bonjour, <span className="italic text-or">{user.prenom || "voyageur"}</span>
          </h1>
          <p className="mt-1.5 text-sm text-parchemin/60 font-mono">{user.email}</p>
        </div>
        <button
          onClick={onLogout}
          data-testid="compte-logout"
          className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-parchemin/50 hover:text-oxyde transition"
        >
          <LogOut className="w-3 h-3" />
          Déconnexion
        </button>
      </div>

      <h2 className="flex items-center gap-2 font-display text-xl text-parchemin mt-8 mb-4">
        <Ticket className="w-4 h-4 text-or" strokeWidth={1.5} />
        Mes réservations
      </h2>

      {loading ? (
        <p className="text-parchemin/50 text-sm font-mono uppercase tracking-widest">Chargement…</p>
      ) : resas.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-16 text-parchemin/50 border border-or/10 border-dashed rounded-sm bg-noir-50/30"
          data-testid="compte-empty"
        >
          <Compass className="w-7 h-7 text-or/50 mb-3" />
          <p className="text-sm italic font-display">Aucune aventure réservée pour l'instant.</p>
          <button
            onClick={() => navigate("/reserver")}
            data-testid="compte-cta-reserver"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-or hover:bg-or-100 text-noir text-sm font-medium transition"
          >
            Choisir mon aventure
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {resas.map((r) => {
            const code = r.sessions?.[0]?.codeAcces;
            return (
              <div
                key={r.id}
                className="bg-noir-50/50 border border-or/15 hover:border-or/40 rounded-sm p-5 transition"
                data-testid={`resa-card-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-lg text-parchemin leading-tight">
                    {r.parcours?.nom || "Aventure"}
                  </h3>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider border ${
                      STATUT_TONE[r.statut] || STATUT_TONE.EN_ATTENTE
                    }`}
                  >
                    {r.statut}
                  </span>
                </div>
                <div className="text-xs font-mono text-parchemin/55 mt-3 flex flex-wrap gap-x-4 gap-y-1">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-or/60" />
                    {r.parcours?.ville || "—"}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="w-3 h-3 text-or/60" />
                    {r.dateJeu ? new Date(r.dateJeu).toLocaleDateString("fr-FR") : "—"}
                  </span>
                  <span>{r.nbJoueurs} joueurs</span>
                  <span className="text-or">{Number(r.montantTTC).toFixed(2)}€</span>
                </div>

                {code ? (
                  <div className="mt-4 pt-4 border-t border-or/10">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-parchemin/50">
                          Code d'accès
                        </div>
                        <div className="font-mono text-lg text-or tracking-[0.25em] mt-0.5">
                          {code}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/jouer/${code}`)}
                        data-testid={`resa-jouer-${r.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-or hover:bg-or-100 text-noir text-sm font-medium transition"
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                        Reprendre
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t border-or/10 text-xs font-mono text-parchemin/45 italic">
                    {r.statut === "PAYE"
                      ? "Code d'accès disponible dès l'activation du jeu."
                      : "Le code d'accès apparaîtra une fois le paiement confirmé."}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const inputCls =
  "w-full px-3.5 py-3 rounded-sm bg-noir-100 border border-or/20 text-parchemin text-sm outline-none focus:border-or focus:ring-1 focus:ring-or/30 transition";

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">{label}</label>
      {children}
    </div>
  );
}
